<template>

<fragment>

    <app-view-sheet-head 
		:fields="fields" 
		:filter="filter" 
		:grid="grid" 
		:fixed="fixed"
		:gridSticky="gridSticky" 
		:stickyFields="stickyFields" 
		:is-scrolled="scrollTop > 0" 
		:left="scrollLeft" 
	/>

	<div class="content-list-wrapper" :class="{'has-search': $route.meta.hasSearch}">

		<div 
			class="content-list-offset is-top" 
			v-observe-visibility="{
				callback: onTopVisibilityChange, 
				intersection: { 
					rootMargin: ($isMobile) ? '-122px 0px 0px 0px' : '-64px 0px 0px 0px' }
				}
			">
		</div>

		<div class="content-sticky" :style="{marginTop: (0 - scrollTop).toString() + 'px'}">

			<slot name="sticky">

			</slot>

		</div>

		<component :is="(window.is.mobile) ? 'div' : 'simplebar'" force-visible
			class="content-list" 
			ref="list" 
			v-bind:class="{
				'is-flush': flush, 
				'can-wide': canWide, 
				'is-selecting': selectActive, 
				'is-fixed': fixed, 
				'is-full': full, 
				'is-cards': cards
			}" 
			v-on:scroll="onScroll"
		>

			<div class="content-list-inner" ref="inner">

				<slot></slot>

			</div>

		</component>

		<div class="content-list-offset is-bottom" v-observe-visibility="onBottomVisibilityChange"></div>

		<div class="content-shade is-left" :class="{'is-active': scrollLeft > 0}" />
		<div class="content-shade is-right" :class="{'is-active': scrollLeft < maxScrollLeft}" />
		<div class="content-shade is-bottom" :class="{'is-active': scrollTop < maxScrollTop}" v-if="$isDesktop" />

	</div>

</fragment>

</template>

<script>

import 'simplebar-core/dist/simplebar.css'
import simplebar from 'simplebar-vue'

export default {

	props: ['flush', 'full', 'fields', 'grid', 'cards', 'canWide', 'filter', 'gridSticky', 'stickyFields'],

	components: {
		'simplebar': simplebar
	},

	data: function() {
	
		return {
			topVisible: false,
			bottomVisible: false,
			scrollLeft: 0,
			scrollWidth: 0,
			clientWidth: 0,
			scrollHeight: 0,
			clientHeight: 0,
			scrollTop: 0,
			fixed: false,

			// internal observers
			ro: null,
			mo: null,
			raf: 0
		}

	},

	mounted: function() {

		this.$nextTick(function() {

			this.setupObservers()
			this.scheduleMeasure()

		}.bind(this))

		window.addEventListener('resize', this.scheduleMeasure, { passive: true })

	},

	beforeDestroy: function() {

		window.removeEventListener('resize', this.scheduleMeasure)

		if (this.ro) this.ro.disconnect()
		if (this.mo) this.mo.disconnect()

		if (this.raf) cancelAnimationFrame(this.raf)

	},

	watch: {

		grid: function() {

			this.scheduleMeasure()

		},

		fields: function() {

			this.scheduleMeasure()

		},

		canWide: function() {

			this.scheduleMeasure()

		},

		cards: function() {

			this.scheduleMeasure()

		},

		full: function() {

			this.scheduleMeasure()

		},

		flush: function() {

			this.scheduleMeasure()

		}

	},

	computed: {

		selectActive: function() {

			return this.$store.getters['service/select/count']

		},

		maxScrollLeft: function() {

			return Math.max(0, this.scrollWidth - this.clientWidth)

		},

		maxScrollTop: function() {

			return Math.max(0, this.scrollHeight - this.clientHeight)

		}

	},

	methods: {

		isUsingSimplebar: function() {

			return !this.window.is.mobile

		},

		getSimplebarInstance: function() {

			// only valid on desktop where <simplebar> is used
			if (!this.isUsingSimplebar()) return null

			var sb = this.$refs.list
			if (!sb) return null

			// simplebar-vue most common: sb.simplebar is the instance
			if (sb.simplebar && sb.simplebar.recalculate) return sb.simplebar

			// some builds expose getSimpleBar()
			if (sb.getSimpleBar && sb.getSimpleBar()) {
				var inst = sb.getSimpleBar()
				if (inst && inst.recalculate) return inst
			}

			// some expose SimpleBar, but sometimes that's the class - still guard
			if (sb.SimpleBar && sb.SimpleBar.recalculate) return sb.SimpleBar

			return null

		},

		simplebarRecalc: function() {

			// never try on mobile (component is a div)
			if (this.window.is.mobile) return

			var sb = this.$refs.list
			if (!sb) return

			// if wrapper hasn't initialised yet, these can be missing
			// (some versions expose getScrollElement but internal instance isn't ready)
			if (sb.getScrollElement) {
				try {
					var el = sb.getScrollElement()
					if (!el) return
				} catch (e) {
					return
				}
			}

			var inst = this.getSimplebarInstance()
			if (!inst || !inst.recalculate) return

			try {

				inst.recalculate()

			} catch (e) {

				// swallow - simplebar-vue can throw during init/unmount swaps
				return

			}

		},

		getScrollEl: function() {

			var sb = this.$refs.list
			if (!sb) return null

			// desktop: ask simplebar-vue for the scroll element
			if (this.isUsingSimplebar()) {

				if (sb.getScrollElement) {
					var el = sb.getScrollElement()
					if (el && (el instanceof Element)) return el
				}

				// fallback: find wrapper
				var rootEl = sb.$el
				if (rootEl && (rootEl instanceof Element)) {
					var wrap = rootEl.querySelector('.simplebar-content-wrapper')
					if (wrap && (wrap instanceof Element)) return wrap
					return rootEl
				}

				return null

			}

			// mobile: ref is a plain div
			if (sb && (sb instanceof Element)) return sb
			if (sb.$el && (sb.$el instanceof Element)) return sb.$el

			return null

		},

		getResizeEl: function() {

			var sb = this.$refs.list
			if (!sb) return null

			// desktop: observe the root element for layout changes
			if (this.isUsingSimplebar()) {
				var rootEl = sb.$el
				if (rootEl && (rootEl instanceof Element)) return rootEl
				return null
			}

			// mobile: observe the scrolling div itself
			var el = this.getScrollEl()
			if (el && (el instanceof Element)) return el

			return null

		},

		// read DOM and sync reactive state
		measure: function() {

			var el = this.getScrollEl()
			if (!el) return

			this.scrollLeft = el.scrollLeft
			this.scrollWidth = el.scrollWidth
			this.clientWidth = el.clientWidth
			this.scrollTop = el.scrollTop
			this.scrollHeight = el.scrollHeight
			this.clientHeight = el.clientHeight

		},

		// coalesce multiple updates into one rAF tick (+ nextTick for Vue DOM flush)
		scheduleMeasure: function() {

			if (this.raf) cancelAnimationFrame(this.raf)

			this.raf = requestAnimationFrame(function() {

				this.raf = 0

				this.$nextTick(function() {

					// allow CSS/layout to settle (grid-template-columns changes, font load, etc)
					requestAnimationFrame(function() {

						// only recalc when simplebar exists
						this.simplebarRecalc()
						this.measure()

					}.bind(this))

				}.bind(this))

			}.bind(this))

		},

		setupObservers: function() {

			// clean up if re-called
			if (this.ro) { this.ro.disconnect(); this.ro = null }
			if (this.mo) { this.mo.disconnect(); this.mo = null }

			var resizeEl = this.getResizeEl()
			var innerEl = this.$refs.inner

			if (resizeEl && !(resizeEl instanceof Element)) resizeEl = null
			if (innerEl && !(innerEl instanceof Element)) innerEl = null

			// ResizeObserver: catches box size changes
			if (resizeEl && window.ResizeObserver) {

				this.ro = new ResizeObserver(function() {

					this.scheduleMeasure()

				}.bind(this))

				this.ro.observe(resizeEl)

			}

			// MutationObserver: watch YOUR slot DOM for:
			// - child changes
			// - attribute changes (style/class) which is what grid-template-columns usually is
			if (innerEl) {

				this.mo = new MutationObserver(function() {

					this.scheduleMeasure()

				}.bind(this))

				this.mo.observe(innerEl, { 
					childList: true, 
					subtree: true,
					attributes: true,
					attributeFilter: ['style', 'class']
				})

			}

		},
	
		onScroll: function(e) {

			// onScroll is attached to:
			// - desktop: simplebar scroll element emits scroll events (target is wrapper)
			// - mobile: div itself emits scroll events
			this.scrollLeft = e.target.scrollLeft
			this.scrollWidth = e.target.scrollWidth
			this.scrollTop = e.target.scrollTop
			this.scrollHeight = e.target.scrollHeight
			this.clientWidth = e.target.clientWidth
			this.clientHeight = e.target.clientHeight

			this.$emit('scrollleft', e.target.scrollLeft)

		},

		onTopVisibilityChange: function(visible) {

			this.topVisible = visible
			this.fixed = !this.topVisible
			this.$emit('fixed', !this.topVisible)

			// visibility shifts can affect layout
			this.scheduleMeasure()
		
		},
	
		onBottomVisibilityChange: function(visible) {
		
			this.bottomVisible = visible

			// bottom visibility changes can affect layout
			this.scheduleMeasure()

		}

	}

}

</script>

<style scoped>

.content-sticky {
	position: absolute;
	left: 0px;
	top: 0px;
	background-color: #fff;
	z-index: 1;
	box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}

.content-sticky:empty {
	display: none;
}

.content-list-wrapper {
	border: 1px solid #ddd;
	overflow: hidden;
	border-radius: 0px 0px 4px 4px;
	border-radius: 0px;
	border-left: 0px;
	border-right: 0px;
	bottom-bottom: 0px;
}

.is-mobile .content-list-wrapper.has-search {
    padding-bottom: 40px;
}

.content-list.can-wide {
	overflow-x: auto;
	overflow-y: visible;
}

.content-list-offset {
	right: 0px;
	height: 32px;
	position: absolute;
	left: 0px;
	pointer-events: none;
}

.content-list-offset.is-top {
	top: -32px;
}

.content-list-offset.is-bottom {
	bottom: -32px;
}

.content-list {
	background-color: #fff;
	overflow: auto;
}

.is-mobile .content-list {
	overflow-x: auto;
    overflow-y: visible;
}

.is-desktop .content-list {
	height: 100%;
}

.content-list-inner {
	display: flex;
  	flex-direction: column;
	align-items: flex-start;
}

.content-shade {
	position: absolute;
	top: 0px;
	bottom: 0px;
	opacity: 0;
	background-color: transparent;
	width: 10px;
	box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}

.content-shade.is-active {
	opacity: 1;
}

.is-mobile .content-shade.is-left {
	left: -20px;
	transition: left 100ms linear;
}

.content-shade.is-active.is-left  {
	left: -10px;
}

.is-mobile .content-shade.is-right {
	right: -20px;
	transition: right 100ms linear;
}

.content-shade.is-active.is-right  {
	right: -10px;
}

.content-shade.is-active.is-bottom  {
	right: 0px;
	left: 0px;
	top: auto;
	bottom: -10px;
	width: 100%;
	height: 10px;
	box-shadow: rgba(0, 0, 0, 0.15) 0px -2px 10px;
}

.content-list >>> .simplebar-track.simplebar-horizontal {
	height: 16px!important;
}

.content-list >>> .simplebar-track.simplebar-horizontal .simplebar-scrollbar {
	height: 10px!important;
	top: auto;
	bottom: 2px;
	background-color: #999!important;
	border-radius: 10px;
}

.content-list >>> .simplebar-track.simplebar-horizontal .simplebar-scrollbar:before {
	display: none;
}

</style>
